import LessonButton from 'app/components/call/BookingDetails/LessonBookingDetails/LessonButton';
import { useI18n } from 'app/components/localization';
import SvgHourglass from 'app/components/svgs/Hourglass';
import { useCountdownTick } from 'app/components/utilities/time';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { useCallBreakpoint } from 'app/containers/call/CallBreakpoint';
import { useCallStateContext } from 'app/containers/call/CallStateContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingType } from 'app/models/rtc/booking/constants';
import { ILessonUIState, LessonButtonPositionStage, LessonPhase } from 'app/models/ui/call/lesson/constants';
import { useCss } from 'app/utils/css';
import React, { FC, ReactElement } from 'react';

interface IProps {
  lessonUIState: ILessonUIState;
  lockTime?: Date;
}

const EndLessonNotifierPresenter: FC<IProps> = ({ lessonUIState, lockTime }) => {
  const css = useCss();
  const { t } = useI18n();

  const inner: ReactElement =
    lockTime !== undefined && lessonUIState.phase >= LessonPhase.ImpendingEnd ? (
      <Countdown lockTime={lockTime} />
    ) : (
      <span>{t('lesson-booking-details.end-lesson-notifier.message')}</span>
    );

  const color = COLOR_PALETTE.grey.white;

  return (
    <div
      className={css('container', {
        display: 'flex',
        justifyContent: 'center',
      })}
    >
      <div
        className={css('content', {
          background: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '30px',
          color,
          fontSize: '16px',
          padding: '10px 10px 10px 20px',
        })}
      >
        <div className={css('flex', { alignItems: 'center', display: 'flex' })} role="alert">
          <SvgHourglass color={color} height="auto" width="10px" />
          <div className={css('inner', { flexShrink: 2, margin: '0 15px' })}>{inner}</div>
          <LessonButton lessonUIState={lessonUIState} />
        </div>
      </div>
    </div>
  );
};

const Countdown: FC<{ lockTime: Date }> = ({ lockTime }) => {
  const { tx } = useI18n();
  const now = useCountdownTick().valueOf();
  const count = Math.max(0, Math.ceil((lockTime.valueOf() - now) / 1000));
  const css = useCss();

  return (
    <span>
      {tx('lesson-booking-details.end-lesson-notifier.countdown', {
        count,
        remaining: <span className={css('countdown', { color: COLOR_PALETTE.red.base })}>{count}</span>,
      })}
    </span>
  );
};

const EndLessonNotifier: FC = () => {
  const bookingState = useBookingState();
  const { lockTime } = useCallStateContext();
  const { lessonUIState } = useCallBreakpoint();

  // Only show the notification is that is where the button is positioned.
  if (
    bookingState?.bookingType !== BookingType.Lesson ||
    lessonUIState.buttonPosition !== LessonButtonPositionStage.Notification
  ) {
    return null;
  }

  return <EndLessonNotifierPresenter lessonUIState={lessonUIState} lockTime={lockTime} />;
};

export default EndLessonNotifier;
